/**
 * Consult Scheduling — Firestore + Cloud-Function SEAMS (impure layer).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 *  The thin, credential-aware boundary between the PURE consulting layer
 *  (`types.ts` model, `availability.ts` engine, `slotView.ts` view helpers —
 *  all SDK-free + unit-tested) and the live backend. Every function here
 *  LAZY-IMPORTS the Firebase SDK exactly like `lib/flags.ts` does, so the pure
 *  layer never drags Firebase into the Node test runner and the module stays
 *  invisible/inert until the flag is on.
 *
 *  CREDENTIAL REALITY (Stage 1): Google Calendar is NOT wired yet. So:
 *    · `fetchFreeBusy` returns [] (everyone shows as fully free) until the
 *      Google free/busy Cloud Function exists to feed it real busy blocks.
 *    · `bookConsultation` is a PLACEHOLDER that writes NOTHING. Per Vex,
 *      booking is server-mediated (a callable Cloud Function creates the
 *      calendar event + Meet link + the `consultations/{id}` doc atomically),
 *      so the client never writes a booking directly. Until that callable is
 *      deployed, this seam returns a friendly "not connected" result the UI
 *      surfaces honestly — no silent half-booking.
 *
 *  Listeners follow the `flags.ts` subscribe shape: emit a safe default
 *  immediately, then live-update; any error keeps the default; return an
 *  unsubscribe fn.
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  DEFAULT_AVAILABILITY,
  type AppointmentType,
  type Audience,
  type AvailabilitySettings,
} from './types';
import type { FreeBusyInterval } from './availability';

/* ════════════════════ appointmentTypes/{id} ════════════════════ */

/**
 * Subscribe to the ACTIVE appointment types (Door A type dropdown source).
 * Emits [] immediately, then the live `appointmentTypes` collection filtered
 * to `active == true`, ascending by name. Errors → []. Returns unsubscribe.
 */
export function listenAppointmentTypes(
  cb: (types: AppointmentType[]) => void,
): () => void {
  cb([]);
  let unsub: (() => void) | null = null;
  let cancelled = false;
  void (async () => {
    try {
      const [{ db }, { collection, onSnapshot, query, where, orderBy }] =
        await Promise.all([
          import('../../../lib/firebase'),
          import('firebase/firestore'),
        ]);
      if (cancelled) return;
      unsub = onSnapshot(
        query(
          collection(db, 'appointmentTypes'),
          where('active', '==', true),
          orderBy('name', 'asc'),
        ),
        (snap) => {
          cb(
            snap.docs.map(
              (d) => ({ ...(d.data() as AppointmentType), id: d.id }),
            ),
          );
        },
        () => cb([]),
      );
    } catch {
      /* SDK unavailable → stays empty */
    }
  })();
  return () => {
    cancelled = true;
    if (unsub) unsub();
  };
}

/* ════════════════════ settings/availability ════════════════════ */

/**
 * Subscribe to the single `settings/availability` doc (global windows + booking
 * rules). Emits the locked DEFAULT_AVAILABILITY seed immediately so the panel
 * renders real slots with zero round-trip, then live-updates once staff have
 * saved their own windows. A missing doc or any error keeps the seed. Returns
 * unsubscribe.
 */
export function listenAvailabilitySettings(
  cb: (settings: AvailabilitySettings) => void,
): () => void {
  cb(DEFAULT_AVAILABILITY);
  let unsub: (() => void) | null = null;
  let cancelled = false;
  void (async () => {
    try {
      const [{ db }, { doc, onSnapshot }] = await Promise.all([
        import('../../../lib/firebase'),
        import('firebase/firestore'),
      ]);
      if (cancelled) return;
      unsub = onSnapshot(
        doc(db, 'settings', 'availability'),
        (snap) => {
          cb(
            snap.exists()
              ? (snap.data() as AvailabilitySettings)
              : DEFAULT_AVAILABILITY,
          );
        },
        () => cb(DEFAULT_AVAILABILITY),
      );
    } catch {
      /* SDK unavailable → stays on the seed */
    }
  })();
  return () => {
    cancelled = true;
    if (unsub) unsub();
  };
}

/* ════════════════════ Google free/busy SEAM ════════════════════ */

/** Raw shape of one busy block returned by the `getConsultAvailability` callable. */
interface GetConsultAvailabilityResult {
  busy: Array<{ startISO: string; endISO: string }>;
  calendarTimeZone: string | null;
}

/** What `fetchFreeBusy` resolves to — busy blocks plus a soft load status. */
export interface FreeBusyResult {
  /** Busy intervals for the engine. Empty = treat the calendar as fully free. */
  busy: FreeBusyInterval[];
  /**
   * 'ok'          → live data fetched (may legitimately be empty);
   * 'unavailable' → the callable failed (not deployed yet / network / auth) and
   *                 we fell back to an empty calendar so Door A keeps rendering.
   */
  status: 'ok' | 'unavailable';
  /** The calendar's IANA zone from the server, when known. */
  calendarTimeZone: string | null;
  /** Soft, user-facing note when `status === 'unavailable'`. */
  message?: string;
}

/**
 * Fetch the busy intervals for a date range from Mack's staff-gated callable
 * `getConsultAvailability` (region asia-south1, defaults the calendarId to
 * vastuarchitect@aayatanveda.com server-side), normalised to the
 * `FreeBusyInterval[]` the pure `computeOpenSlots` engine already consumes. The
 * SDK is lazy-imported (the proven portal callable pattern, e.g. messagesStore),
 * so the pure layer never drags Firebase into the Node test runner.
 *
 * ROBUSTNESS: the function is not deployed yet, so today's call WILL fail. Any
 * error (not-deployed / network / unauthenticated) is caught and we return an
 * empty `busy` with `status: 'unavailable'` + a soft message — Door A keeps
 * rendering every in-window slot against an empty calendar rather than crashing.
 * Once the CF is live the SAME code returns real busy blocks with `status: 'ok'`,
 * no UI change required.
 */
/** Hard upper bound on the free/busy fetch. A staff callable that never settles
 *  (an unforeseen network stall) must not strand Door A's picker — it resolves
 *  to the honest 'unavailable' fallback so the "Retry" affordance appears. */
const FREEBUSY_TIMEOUT_MS = 12_000;

export async function fetchFreeBusy(
  rangeStartISO: string,
  rangeEndISO: string,
): Promise<FreeBusyResult> {
  // Honest degraded result — used for both a thrown error AND a hard timeout so
  // the picker always resolves to a real, retryable state (never an eternal load).
  const fallback: FreeBusyResult = {
    busy: [],
    status: 'unavailable',
    calendarTimeZone: null,
    message: "Couldn't load live availability — showing all window slots for now.",
  };
  try {
    const live = (async (): Promise<FreeBusyResult> => {
      const [{ functions }, { httpsCallable }] = await Promise.all([
        import('../../../lib/firebase'),
        import('firebase/functions'),
      ]);
      const fn = httpsCallable<
        { timeMinISO: string; timeMaxISO: string; calendarId?: string },
        GetConsultAvailabilityResult
      >(functions, 'getConsultAvailability');
      const res = await fn({ timeMinISO: rangeStartISO, timeMaxISO: rangeEndISO });
      const data = res.data;
      // Defensive normalisation: keep only well-formed {startISO,endISO} blocks.
      const busy: FreeBusyInterval[] = Array.isArray(data?.busy)
        ? data.busy
            .filter(
              (b) =>
                b &&
                typeof b.startISO === 'string' &&
                typeof b.endISO === 'string',
            )
            .map((b) => ({ startISO: b.startISO, endISO: b.endISO }))
        : [];
      return {
        busy,
        status: 'ok',
        calendarTimeZone: data?.calendarTimeZone ?? null,
      };
    })();

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeoutP = new Promise<FreeBusyResult>((resolve) => {
      timer = setTimeout(() => resolve(fallback), FREEBUSY_TIMEOUT_MS);
    });
    const result = await Promise.race([live, timeoutP]);
    if (timer) clearTimeout(timer);
    return result;
  } catch {
    // Not deployed / offline / not a staff session → degrade gracefully.
    return fallback;
  }
}

/* ════════════════════ booked consultations (double-booking guard) ═══════════ */

/**
 * The slot-start instants of EXISTING consultations in a range — the slot
 * picker drops these (Vex note 2026-07-03: idempotency is per lead+slot
 * server-side, so two different leads CAN book the same slot; and
 * getConsultAvailability may not reflect a just-booked slot). Reads the
 * `consultations` collection directly — team-READABLE since Vex's rules
 * deploy (writes stay CF-only). DEFENSIVE field read (the CF owns the doc
 * shape): accepts `slotStartIso` / `slotStartISO` / `slotStart`, skips
 * cancelled docs, tolerates any error with [] (a read hiccup must never
 * blank the picker — the server-side idempotency remains the hard guard).
 */
export async function fetchBookedSlotStarts(
  rangeStartISO: string,
  rangeEndISO: string,
): Promise<string[]> {
  try {
    const [{ db }, { collection, getDocs }] = await Promise.all([
      import('../../../lib/firebase'),
      import('firebase/firestore'),
    ]);
    const snap = await getDocs(collection(db, 'consultations'));
    const lo = Date.parse(rangeStartISO);
    const hi = Date.parse(rangeEndISO);
    const out: string[] = [];
    for (const d of snap.docs) {
      const r = d.data() as Record<string, unknown>;
      if (r.status === 'cancelled') continue;
      const raw = r.slotStartIso ?? r.slotStartISO ?? r.slotStart;
      if (typeof raw !== 'string') continue;
      const t = Date.parse(raw);
      if (!Number.isFinite(t)) continue;
      if (Number.isFinite(lo) && t < lo) continue;
      if (Number.isFinite(hi) && t > hi) continue;
      out.push(raw);
    }
    return out;
  } catch {
    return [];
  }
}

/* ════════════════════ bookConsultation SEAM (REAL — Phase 2) ════════════════════ */

/**
 * How the consult happens — Mack's `createConsultBooking` contract vocabulary.
 * `video` mints a Google Meet link; `call` is a phone call; `office` is at the
 * AV office (address backend-supplied); `site` is at the client's place (an
 * OPTIONAL free-text `siteLocation` may accompany it).
 */
export type ConsultMode = 'video' | 'call' | 'office' | 'site';

/** What the Door A panel hands the booking seam when the staffer taps "Book". */
export interface BookConsultationInput {
  /** Lead this booking belongs to (null for a cold booking). FK → leads/{id}. */
  leadId: string | null;
  /** Chosen appointment type. FK → appointmentTypes/{id}. */
  typeId: string;
  /** The picked slot (ISO instants, anchored to `timeZone`). */
  slotStartISO: string;
  slotEndISO: string;
  /** The audience the staffer was viewing when they picked. */
  audience: Audience;
  /** IANA zone the slot is anchored in (the bookable truth, e.g. Asia/Kolkata). */
  timeZone: string;
  /** How the consult happens — Mack's contract vocabulary. */
  consultType: ConsultMode;
  /** Slot length in minutes (contract default 30 when absent). */
  durationMin?: number;
  /** Optional staffer note carried onto the calendar event / consultation doc. */
  notes?: string;
  /** OPTIONAL free-text site address — only meaningful when consultType==='site'.
   *  Booking must never be blocked when it's absent (the client just sees "Your
   *  site"). Sent only for 'site' + non-empty. */
  siteLocation?: string;
}

/** Outcome the panel renders. `status` drives the banner styling/copy. */
export interface BookConsultationResult {
  ok: boolean;
  status: 'not_connected' | 'booked' | 'error';
  message: string;
  /** Set only on a real `booked` outcome. */
  consultationId?: string;
  /** Google Meet link (video consults) — shown on the lead + the banner. */
  meetLink?: string | null;
}

/** The wire payload Mack's `createConsultBooking` callable takes (contract,
 *  2026-07-03): { leadId, slotStartIso, durationMin (default 30),
 *  type: 'video'|'call', notes? }. Note the contract's `slotStartIso` casing. */
export interface CreateConsultBookingPayload {
  leadId: string | null;
  slotStartIso: string;
  durationMin: number;
  type: ConsultMode;
  notes?: string;
  /** Optional site address — sent ONLY for `type: 'site'` and when non-empty. */
  siteLocation?: string;
}

/** What the callable returns (contract): { ok, consultationId, meetLink?,
 *  calendarEventId?, status: 'booked'|'not_connected'|'error', reason? }. */
interface CreateConsultBookingResponse {
  ok: boolean;
  consultationId?: string;
  meetLink?: string;
  calendarEventId?: string;
  status: 'booked' | 'not_connected' | 'error';
  reason?: string;
}

/** PURE — panel input → the callable's wire payload (unit-tested). */
export function toCreateConsultBookingPayload(
  input: BookConsultationInput,
): CreateConsultBookingPayload {
  const ms = Date.parse(input.slotEndISO) - Date.parse(input.slotStartISO);
  const derived = Number.isFinite(ms) && ms > 0 ? Math.round(ms / 60_000) : 30;
  return {
    leadId: input.leadId,
    slotStartIso: input.slotStartISO,
    durationMin: input.durationMin ?? derived,
    type: input.consultType,
    ...(input.notes && input.notes.trim() ? { notes: input.notes.trim() } : {}),
    // Site address rides along ONLY for a site visit with a non-empty value;
    // absent → the client just sees "Your site" (booking never blocked).
    ...(input.consultType === 'site' && input.siteLocation && input.siteLocation.trim()
      ? { siteLocation: input.siteLocation.trim() }
      : {}),
  };
}

/** PURE — the callable's response → the panel's render outcome (unit-tested).
 *  Defensive: a garbage / partial payload reads as an honest error, never a
 *  fake "booked". */
export function mapCreateConsultBookingResponse(
  raw: unknown,
): BookConsultationResult {
  const r = (raw ?? {}) as Partial<CreateConsultBookingResponse>;
  if (r.status === 'booked' && r.ok === true) {
    return {
      ok: true,
      status: 'booked',
      consultationId:
        typeof r.consultationId === 'string' ? r.consultationId : undefined,
      meetLink: typeof r.meetLink === 'string' && r.meetLink ? r.meetLink : null,
      message: r.meetLink
        ? 'Booked — calendar event created and the Meet link is on the lead.'
        : 'Booked — the calendar event is created.',
    };
  }
  if (r.status === 'not_connected') {
    return {
      ok: false,
      status: 'not_connected',
      message:
        "Booking isn't connected yet — calendar setup is pending. Nothing was saved. " +
        'You can still stamp the consult date manually below.',
    };
  }
  return {
    ok: false,
    status: 'error',
    message: r.reason
      ? `Booking failed: ${r.reason}. Nothing was saved — try again, or stamp the date manually.`
      : 'Booking failed — nothing was saved. Try again, or stamp the date manually.',
  };
}

/**
 * REAL booking seam (Phase 2, 2026-07-03) — calls Mack's server-mediated
 * `createConsultBooking` callable (per Vex: the CF creates the Google Calendar
 * event + Meet link + `consultations/{id}` atomically with the service-account
 * credential; the client never writes a booking directly).
 *
 * On a REAL `booked` outcome the lead advances to `consult_booked` through the
 * ONE state machine (pure `leads.recordConsultBooked` — legal `transitionLead`
 * hops only), `consultBookedAt` is stamped with the SLOT date (the calendar
 * lens buckets on it) and the Meet link / consultationId land on the lead.
 *
 * DEGRADES HONESTLY: the callable not being deployed yet / offline / an auth
 * refusal maps to `not_connected` — the panel keeps its friendly "calendar
 * setup pending" banner and the v1 manual date-stamp stays available (never a
 * dead control). `callCF` is injectable (localChat pattern) so the seam is
 * unit-testable without Firebase.
 */
export async function bookConsultation(
  input: BookConsultationInput,
  callCF?: (
    payload: CreateConsultBookingPayload,
  ) => Promise<CreateConsultBookingResponse>,
): Promise<BookConsultationResult> {
  let invoke = callCF;
  if (!invoke) {
    try {
      const [{ functions }, { httpsCallable }] = await Promise.all([
        import('../../../lib/firebase'),
        import('firebase/functions'),
      ]);
      const fn = httpsCallable<
        CreateConsultBookingPayload,
        CreateConsultBookingResponse
      >(functions, 'createConsultBooking');
      invoke = async (payload) => (await fn(payload)).data;
    } catch {
      return mapCreateConsultBookingResponse({ status: 'not_connected' });
    }
  }

  let result: BookConsultationResult;
  try {
    result = mapCreateConsultBookingResponse(
      await invoke(toCreateConsultBookingPayload(input)),
    );
  } catch {
    // Not deployed / offline / not a staff session → the honest pending state.
    result = mapCreateConsultBookingResponse({ status: 'not_connected' });
  }

  // ── EVENT-DRIVEN STAGE MOVE (streamline Phase 1b seam, now live) ──
  // A REAL booked outcome advances the lead via the ONE state machine and
  // carries the Meet link / consultationId onto the record. Best-effort: a
  // successful booking is never reported failed because the lead stamp
  // hiccuped (applyConsultBookedToLead never throws).
  if (result.ok && result.status === 'booked' && input.leadId) {
    await applyConsultBookedToLead(input.leadId, input.slotStartISO, undefined, {
      meetLink: result.meetLink ?? null,
      consultationId: result.consultationId ?? null,
    });
  }
  return result;
}

/* ════════════════════ Lead stage seam (streamline Phase 1b) ════════════════════ */

/**
 * Apply a REAL consult booking to the lead: `consultBookedAt` stamped +
 * state advanced to `consult_booked` via the ONE state machine (pure
 * `recordConsultBooked` walks legal `transitionLead` hops; a lead already
 * past that stage only gets the date stamp — never dragged backward).
 * Persisted through the whitelisted `leadStore.updateLead` path.
 *
 * Lazy-imports (the flags.ts pattern) so the pure consulting layer never
 * drags Firebase into the Node test runner. Never throws — a booking that
 * succeeded must not be reported as failed because the lead stamp hiccuped;
 * returns whether the lead update landed.
 */
export async function applyConsultBookedToLead(
  leadId: string,
  slotStartISO: string,
  actorName: string = 'Consult booking',
  extras?: { meetLink?: string | null; consultationId?: string | null },
): Promise<boolean> {
  try {
    const [{ recordConsultBooked }, { getLead, updateLead }] = await Promise.all([
      import('../../../lib/leads'),
      import('../../../lib/leadStore'),
    ]);
    const lead = await getLead(leadId);
    if (!lead) return false;
    const now = new Date().toISOString();
    let next = recordConsultBooked(lead, slotStartISO, now);
    // Phase 2 — the REAL booking's artefacts ride the record (snapshot-carried
    // fields, no rules change): the Meet link the staff/client join with + the
    // consultations/{id} audit FK. Only stamped when the callable minted them.
    if (extras && (extras.meetLink || extras.consultationId)) {
      next = {
        ...next,
        ...(extras.meetLink ? { consultMeetLink: extras.meetLink } : {}),
        ...(extras.consultationId ? { consultationId: extras.consultationId } : {}),
      };
    }
    await updateLead(next, actorName, `Consult booked → ${next.state} (auto)`);
    return true;
  } catch {
    return false;
  }
}
